import type { ProTableProps } from '@ant-design/pro-components';
import { ProTable as BaseProTable } from '@ant-design/pro-components';
import { useIntl } from '@umijs/max';
import { Button } from 'antd';
import React, { useMemo } from 'react';

/**
 * 封装的 ProTable 组件
 * - 自动限制表格内容区最大高度（默认 65vh），底部元素可显示
 * - 搜索表单「查询」「重置」「展开」「收起」按钮文案使用国际化（含柬文）
 *
 * 完全兼容原 ProTable 的所有 API；可通过 scroll 覆盖默认高度。
 */
function ProTable<T extends Record<string, any>, U extends Record<string, any> = Record<string, any>, ValueType = 'text'>(
  props: ProTableProps<T, U, ValueType>,
) {
  const intl = useIntl();
  const DEFAULT_MAX_HEIGHT = '65vh';

  const scrollConfig = useMemo(() => {
    if (props.scroll) {
      return {
        ...props.scroll,
        y: props.scroll.y !== undefined ? props.scroll.y : DEFAULT_MAX_HEIGHT,
      };
    }
    return { y: DEFAULT_MAX_HEIGHT };
  }, [props.scroll]);

  const submitText = intl.formatMessage({ id: '通用.查询' });
  const resetText = intl.formatMessage({ id: '通用.重置' });
  const expandText = intl.formatMessage({ id: '通用.展开' });
  const collapseText = intl.formatMessage({ id: '通用.收起' });

  const searchWithIntl = useMemo(() => {
    if (props.search === false) return false;
    const base = (typeof props.search === 'object' ? props.search : {}) as Record<string, unknown>;
    const baseSubmitter = (typeof base.submitter === 'object' ? base.submitter : {}) as Record<string, unknown>;
    const baseSearchConfig = (typeof baseSubmitter?.searchConfig === 'object' ? baseSubmitter.searchConfig : {}) as Record<string, unknown>;
    // submitText/resetText 只放在 submitter.searchConfig，避免被透传到 Form 的 DOM 上触发 React 警告
    return {
      ...base,
      collapseRender: (collapsed: boolean) => (collapsed ? expandText : collapseText),
      submitter: {
        ...baseSubmitter,
        searchConfig: {
          ...baseSearchConfig,
          submitText,
          resetText,
        },
      },
      optionRender: (searchConfig: any, formProps: any, dom: React.ReactNode[]) => {
        const customOptionRender = base.optionRender;
        if (typeof customOptionRender === 'function') {
          return customOptionRender(searchConfig, formProps, dom);
        }
        const { form } = formProps ?? {};
        if (!form) return dom;
        return [
          <Button key="reset" onClick={() => form.resetFields()}>
            {resetText}
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {submitText}
          </Button>,
        ];
      },
    };
  }, [props.search, submitText, resetText, expandText, collapseText, intl]);

  return <BaseProTable {...props} scroll={scrollConfig} search={searchWithIntl} />;
}

export default ProTable;
